import moment from "moment";
import "moment/locale/es";
import { categoriasDelReporte, filasDelReporte, ordenesDelReporte, resumenPrimeraPasada, VERDE, ROJO } from "../Pages/Panel/PresupuestosPU/OrdenesDeProduccionPU/PlaneacionProduccionPU/reporteDiaProduccion.utils";

moment.locale("es");

const ANCHO = 752;
const CIAN = "#00BFD6";
const PETROLEO = "#006B7A";
const TINTA = "#1A3A4A";
const GRIS = "#8C8C8C";
const PISTA = "#EAF6F8";
const AZUL_ENC = "#C5E3F6";
const FONDO_CUMPLE = "#D9F7BE";
const FONDO_NO_CUMPLE = "#FFCCC7";

const escapar = (t) => String(t ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const pct = (n) => (n === null || n === undefined ? "—" : `${Math.round(n * 10) / 10}%`);
const miles = (n) => (n || 0).toLocaleString("es-MX");
const recortar = (t, n) => (t.length > n ? `${t.slice(0, n - 1)}…` : t);

const graficaOkVsObjetivo = (filas, ancho) => {
  const fila = 34;
  const izq = 190;
  const der = 150;
  const pista = ancho - izq - der;
  const alto = filas.length * fila + 34;
  const x = (v) => izq + (Math.max(0, Math.min(100, v || 0)) / 100) * pista;
  let svg = `<svg width="${ancho}" height="${alto}" xmlns="http://www.w3.org/2000/svg">`;
  [0, 25, 50, 75, 100].forEach((v) => {
    svg += `<line x1="${x(v)}" y1="4" x2="${x(v)}" y2="${alto - 18}" stroke="#E8E8E8" stroke-width="0.6"/>`;
    svg += `<text x="${x(v)}" y="${alto - 6}" font-size="7" fill="${GRIS}" text-anchor="middle">${v}%</text>`;
  });
  filas.forEach((f, i) => {
    const ok = f.categorias.find((c) => c.etiqueta === "OK");
    const y = 14 + i * fila;
    svg += `<text x="${izq - 8}" y="${y + 8}" font-size="8" font-weight="bold" fill="${TINTA}" text-anchor="end">${escapar(f.folio)} · ${escapar(f.noParte)}</text>`;
    svg += `<text x="${izq - 8}" y="${y + 17}" font-size="6.5" fill="${GRIS}" text-anchor="end">${escapar(recortar(f.descripcion, 40))}</text>`;
    svg += `<rect x="${izq}" y="${y}" width="${pista}" height="16" rx="8" ry="8" fill="${PISTA}"/>`;
    if (ok?.real) svg += `<rect x="${izq}" y="${y}" width="${Math.max(16, x(ok.real) - izq)}" height="16" rx="8" ry="8" fill="${CIAN}"/>`;
    if (ok?.objetivo !== null && ok?.objetivo !== undefined) {
      svg += `<line x1="${x(ok.objetivo)}" y1="${y - 3}" x2="${x(ok.objetivo)}" y2="${y + 19}" stroke="${PETROLEO}" stroke-width="2"/>`;
      svg += `<text x="${x(ok.objetivo)}" y="${y - 4}" font-size="6" fill="${PETROLEO}" text-anchor="middle">obj. ${pct(ok.objetivo)}</text>`;
    }
    svg += `<text x="${izq + pista + 8}" y="${y + 11}" font-size="8.5" font-weight="bold" fill="${TINTA}">${pct(ok?.real)}</text>`;
    svg += `<text x="${izq + pista + 44}" y="${y + 11}" font-size="7" fill="${GRIS}">${miles(ok?.llegaron)} pzs</text>`;
    if (ok?.cumple !== null && ok?.cumple !== undefined) {
      const color = ok.cumple ? VERDE : ROJO;
      svg += `<rect x="${izq + pista + 88}" y="${y + 1}" width="56" height="14" rx="7" ry="7" fill="${color}"/>`;
      svg += `<text x="${izq + pista + 116}" y="${y + 11}" font-size="7" font-weight="bold" fill="#FFFFFF" text-anchor="middle">${ok.cumple ? "CUMPLE" : "NO CUMPLE"}</text>`;
    }
  });
  return `${svg}</svg>`;
};

const graficaDistribucion = (filas, categorias, ancho) => {
  const fila = 24;
  const izq = 110;
  const pista = ancho - izq - 6;
  const alto = filas.length * fila + 26;
  let svg = `<svg width="${ancho}" height="${alto}" xmlns="http://www.w3.org/2000/svg">`;
  filas.forEach((f, i) => {
    const y = 4 + i * fila;
    svg += `<text x="${izq - 6}" y="${y + 11}" font-size="7.5" font-weight="bold" fill="${TINTA}" text-anchor="end">${escapar(f.folio)} · ${escapar(recortar(f.noParte, 14))}</text>`;
    svg += `<rect x="${izq}" y="${y}" width="${pista}" height="16" fill="${PISTA}"/>`;
    let x = izq;
    f.categorias.forEach((dato) => {
      const color = categorias.find((c) => c.etiqueta === dato.etiqueta)?.color || GRIS;
      const w = ((dato.real || 0) / 100) * pista;
      if (w <= 0) return;
      svg += `<rect x="${x}" y="${y}" width="${w}" height="16" fill="${color}"/>`;
      if (w > 34) svg += `<text x="${x + w / 2}" y="${y + 11}" font-size="7" fill="#FFFFFF" text-anchor="middle">${pct(dato.real)} · ${miles(dato.llegaron)}</text>`;
      x += w;
    });
    if (!f.produccionReal) svg += `<text x="${izq + 6}" y="${y + 11}" font-size="7" fill="${GRIS}">Sin producción real</text>`;
  });
  let lx = izq;
  const ly = alto - 12;
  categorias.forEach((c) => {
    svg += `<rect x="${lx}" y="${ly}" width="9" height="9" rx="2" ry="2" fill="${c.color}"/><text x="${lx + 13}" y="${ly + 8}" font-size="7.5" fill="${TINTA}">${escapar(c.etiqueta)}</text>`;
    lx += 26 + c.etiqueta.length * 5;
  });
  return `${svg}</svg>`;
};

const arco = (cx, cy, r, ini, fin) => {
  const p = (a) => [cx + r * Math.cos(a), cy + r * Math.sin(a)];
  const [x1, y1] = p(ini);
  const [x2, y2] = p(fin);
  return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${fin - ini > Math.PI ? 1 : 0} 1 ${x2} ${y2} Z`;
};

const graficaTotal = (filas, categorias, ancho) => {
  const totales = categorias.map((c) => ({ ...c, piezas: filas.reduce((acc, f) => acc + (f.categorias.find((x) => x.etiqueta === c.etiqueta)?.llegaron || 0), 0) }));
  const suma = totales.reduce((acc, t) => acc + t.piezas, 0);
  const alto = 140;
  const cx = 66;
  const cy = 68;
  let svg = `<svg width="${ancho}" height="${alto}" xmlns="http://www.w3.org/2000/svg">`;
  if (!suma) return `${svg}<text x="10" y="70" font-size="8" fill="${GRIS}">Sin producción real</text></svg>`;
  let angulo = -Math.PI / 2;
  totales.forEach((t) => {
    if (!t.piezas) return;
    const fin = angulo + (t.piezas / suma) * Math.PI * 2;
    svg += t.piezas === suma ? `<circle cx="${cx}" cy="${cy}" r="58" fill="${t.color}"/>` : `<path d="${arco(cx, cy, 58, angulo, fin)}" fill="${t.color}" stroke="#FFFFFF" stroke-width="1.5"/>`;
    angulo = fin;
  });
  svg += `<circle cx="${cx}" cy="${cy}" r="36" fill="#FFFFFF"/>`;
  svg += `<text x="${cx}" y="${cy + 2}" font-size="12" font-weight="bold" fill="${TINTA}" text-anchor="middle">${miles(suma)}</text>`;
  svg += `<text x="${cx}" y="${cy + 12}" font-size="6.5" fill="${GRIS}" text-anchor="middle">pzs producidas</text>`;
  totales.forEach((t, i) => {
    const y = 26 + i * 22;
    svg += `<rect x="146" y="${y}" width="10" height="10" rx="2" ry="2" fill="${t.color}"/>`;
    svg += `<text x="162" y="${y + 8}" font-size="8" font-weight="bold" fill="${TINTA}">${escapar(t.etiqueta)}</text>`;
    svg += `<text x="162" y="${y + 17}" font-size="7" fill="${GRIS}">${pct((t.piezas / suma) * 100)} · ${miles(t.piezas)} pzs</text>`;
  });
  return `${svg}</svg>`;
};

const tarjeta = (titulo, valor, color = TINTA) => ({
  stack: [
    { text: titulo.toUpperCase(), fontSize: 6.5, bold: true, color: GRIS, characterSpacing: 0.5 },
    { text: valor, fontSize: 16, bold: true, color, margin: [0, 2, 0, 0] },
  ],
  margin: [8, 6, 8, 6],
});

const tablaReporte = (filas, categorias) => {
  const n = categorias.length;
  const enc = (text, extra = {}) => ({ text, style: "encabezado", fillColor: AZUL_ENC, ...extra });
  const fijas = ["LÍNEA", "CLIENTE", "NO. PARTE", "DESCRIPCIÓN", "ORDEN", "PZS PLANEADAS", "PRODUCCIÓN REAL"];
  const sinBorde = { text: "", border: [false, false, false, false] };
  const fila1 = [
    { ...sinBorde, colSpan: fijas.length },
    ...Array.from({ length: fijas.length - 1 }, () => ({})),
    ...(n ? [{ text: "RESULTADOS", colSpan: n * 2, style: "encabezado", fillColor: "#92D050" }, ...Array.from({ length: n * 2 - 1 }, () => ({}))] : []),
    sinBorde,
    ...(n ? [{ text: "OBJETIVOS", colSpan: n, style: "encabezado", fillColor: "#DA70D6" }, ...Array.from({ length: n - 1 }, () => ({}))] : []),
    sinBorde,
  ];
  const fila2 = [
    ...fijas.map((t) => enc(t, { rowSpan: 2 })),
    ...categorias.flatMap((c) => [enc(c.etiqueta, { colSpan: 2 }), {}]),
    enc("DIF.", { rowSpan: 2 }),
    ...categorias.map((c) => enc(c.etiqueta)),
    enc("CUMP. OBJ.", { rowSpan: 2 }),
  ];
  const fila3 = [
    ...fijas.map(() => ({})),
    ...categorias.flatMap(() => [enc("%"), enc("PZS")]),
    {},
    ...categorias.map(() => enc("%")),
    {},
  ];
  const cuerpo = filas.map((f) => [
    { text: f.linea, style: "celda", bold: true },
    { text: f.cliente || "—", style: "celda", bold: true },
    { text: f.noParte, style: "celda" },
    { text: f.descripcion, style: "celda" },
    { text: f.folio, style: "celda", alignment: "center" },
    { text: miles(f.planeadas), style: "celda", alignment: "right" },
    { text: miles(f.produccionReal), style: "celda", alignment: "right" },
    ...categorias.flatMap((c) => {
      const dato = f.categorias.find((x) => x.etiqueta === c.etiqueta);
      const fillColor = dato?.cumple === false ? FONDO_NO_CUMPLE : dato?.cumple ? FONDO_CUMPLE : undefined;
      return [
        { text: pct(dato?.real), style: "celda", bold: true, alignment: "right", color: dato?.cumple === false ? ROJO : dato?.cumple ? VERDE : undefined, fillColor },
        { text: dato ? miles(dato.llegaron) : "—", style: "celda", alignment: "right", fillColor },
      ];
    }),
    { text: miles(f.dif), style: "celda", alignment: "right", fillColor: "#F8CBAD" },
    ...categorias.map((c) => ({ text: pct(f.categorias.find((x) => x.etiqueta === c.etiqueta)?.objetivo), style: "celda", alignment: "right" })),
    {
      text: f.cumple === null ? "SIN DATOS" : f.cumple ? "CUMPLE" : "NO CUMPLE",
      style: "celda",
      bold: true,
      alignment: "center",
      color: f.cumple === null ? GRIS : "#FFFFFF",
      fillColor: f.cumple === null ? "#F5F5F5" : f.cumple ? VERDE : ROJO,
    },
  ]);
  return {
    table: {
      headerRows: 3,
      widths: [42, 46, 50, "*", 26, 32, 32, ...categorias.flatMap(() => [26, 24]), 26, ...categorias.map(() => 26), 46],
      body: [fila1, fila2, fila3, ...cuerpo],
    },
    layout: {
      hLineColor: () => "#7F7F7F",
      vLineColor: () => "#7F7F7F",
      hLineWidth: () => 0.5,
      vLineWidth: () => 0.5,
      paddingLeft: () => 2,
      paddingRight: () => 2,
      paddingTop: () => 2,
      paddingBottom: () => 2,
    },
  };
};

const graficaPrimeraPasada = (r, ancho) => {
  const fila = 34;
  const izq = 110;
  const der = 150;
  const pista = ancho - izq - der;
  const alto = r.categorias.length * fila + 34;
  const x = (v) => izq + (Math.max(0, Math.min(100, v || 0)) / 100) * pista;
  let svg = `<svg width="${ancho}" height="${alto}" xmlns="http://www.w3.org/2000/svg">`;
  [0, 25, 50, 75, 100].forEach((v) => {
    svg += `<line x1="${x(v)}" y1="8" x2="${x(v)}" y2="${alto - 18}" stroke="#E8E8E8" stroke-width="0.6"/>`;
    svg += `<text x="${x(v)}" y="${alto - 6}" font-size="7" fill="${GRIS}" text-anchor="middle">${v}%</text>`;
  });
  r.categorias.forEach((c, i) => {
    const y = 14 + i * fila;
    svg += `<text x="${izq - 8}" y="${y + 11}" font-size="9" font-weight="bold" fill="${TINTA}" text-anchor="end">${escapar(c.etiqueta)}</text>`;
    svg += `<rect x="${izq}" y="${y}" width="${pista}" height="16" rx="8" ry="8" fill="${PISTA}"/>`;
    if (c.real) svg += `<rect x="${izq}" y="${y}" width="${Math.max(16, x(c.real) - izq)}" height="16" rx="8" ry="8" fill="${c.etiqueta === "OK" ? CIAN : PETROLEO}"/>`;
    if (c.objetivo !== null) {
      svg += `<line x1="${x(c.objetivo)}" y1="${y - 3}" x2="${x(c.objetivo)}" y2="${y + 19}" stroke="${TINTA}" stroke-width="2"/>`;
      svg += `<text x="${x(c.objetivo)}" y="${y - 4}" font-size="6" fill="${TINTA}" text-anchor="middle">${c.tipo === "minimo" ? "mín." : "máx."} ${pct(c.objetivo)}</text>`;
    }
    svg += `<text x="${izq + pista + 8}" y="${y + 11}" font-size="8.5" font-weight="bold" fill="${TINTA}">${pct(c.real)}</text>`;
    svg += `<text x="${izq + pista + 44}" y="${y + 11}" font-size="7" fill="${GRIS}">${miles(c.llegaron)} pzs</text>`;
    if (c.cumple !== null) {
      svg += `<rect x="${izq + pista + 88}" y="${y + 1}" width="56" height="14" rx="7" ry="7" fill="${c.cumple ? VERDE : ROJO}"/>`;
      svg += `<text x="${izq + pista + 116}" y="${y + 11}" font-size="7" font-weight="bold" fill="#FFFFFF" text-anchor="middle">${c.cumple ? "CUMPLE" : "NO CUMPLE"}</text>`;
    }
  });
  return `${svg}</svg>`;
};

const paginaPrimeraPasada = ({ orden, grupo }) => {
  const r = resumenPrimeraPasada(orden, grupo);
  const ok = r.categorias.find((c) => c.etiqueta === "OK");
  const enc = (text) => ({ text, style: "encabezado", fillColor: AZUL_ENC });
  return [
    { text: `FIRST TIME QUALITY — ${r.folio}`, fontSize: 14, bold: true, color: PETROLEO, pageBreak: "before" },
    { text: `${r.noParte} · ${r.descripcion}`, fontSize: 9, color: TINTA, margin: [0, 2, 0, 0] },
    { text: `Línea: ${r.linea} · Cliente: ${r.cliente || "—"}`, fontSize: 8, color: GRIS, margin: [0, 2, 0, 0] },
    { text: "Cada pieza original cuenta una sola vez, en el primer resultado al que llegó.", fontSize: 7, color: GRIS, italics: true, margin: [0, 2, 0, 0] },
    { canvas: [{ type: "line", x1: 0, y1: 4, x2: ANCHO, y2: 4, lineWidth: 2, lineColor: CIAN }], margin: [0, 4, 0, 8] },
    {
      margin: [0, 0, 0, 8],
      table: {
        widths: ["*", "*", "*", "*"],
        body: [
          [
            tarjeta("Piezas planeadas", miles(r.planeadas)),
            tarjeta("Piezas en 1a pasada", `${miles(r.clasificadas)}${r.pendientes ? ` (faltan ${miles(r.pendientes)})` : ""}`),
            tarjeta("% OK primera pasada", pct(ok?.real), ok?.cumple === false ? ROJO : ok?.cumple ? VERDE : CIAN),
            tarjeta("Estado", r.cerrada ? "CERRADA" : "EN CURSO", r.cerrada ? VERDE : "#D48806"),
          ],
        ],
      },
      layout: { fillColor: () => "#F0FBFC", hLineColor: () => "#FFFFFF", vLineColor: () => "#FFFFFF", hLineWidth: () => 4, vLineWidth: () => 4 },
    },
    {
      table: {
        headerRows: 1,
        widths: ["*", 70, 70, 70, 80, 80],
        body: [
          [enc("CATEGORÍA"), enc("PZS"), enc("% REAL"), enc("% OBJETIVO"), enc("TIPO DE OBJETIVO"), enc("CUMPLE")],
          ...r.categorias.map((c) => {
            const fillColor = c.cumple === false ? FONDO_NO_CUMPLE : c.cumple ? FONDO_CUMPLE : undefined;
            return [
              { text: c.etiqueta, style: "celda", bold: true },
              { text: miles(c.llegaron), style: "celda", alignment: "right", fillColor },
              { text: pct(c.real), style: "celda", bold: true, alignment: "right", fillColor, color: c.cumple === false ? ROJO : c.cumple ? VERDE : undefined },
              { text: pct(c.objetivo), style: "celda", alignment: "right" },
              { text: c.objetivo === null ? "—" : c.tipo === "minimo" ? "Mínimo" : "Máximo", style: "celda", alignment: "center" },
              { text: c.cumple === null ? "—" : c.cumple ? "SÍ" : "NO", style: "celda", bold: true, alignment: "center", fillColor },
            ];
          }),
          [
            { text: "TOTAL", style: "celda", bold: true, fillColor: AZUL_ENC },
            { text: miles(r.clasificadas), style: "celda", bold: true, alignment: "right", fillColor: AZUL_ENC },
            { text: r.clasificadas ? "100%" : "—", style: "celda", bold: true, alignment: "right", fillColor: AZUL_ENC },
            { text: "", fillColor: AZUL_ENC },
            { text: "CUMP. OBJ.", style: "celda", bold: true, alignment: "center", fillColor: AZUL_ENC },
            {
              text: r.cumple === null ? "SIN DATOS" : r.cumple ? "CUMPLE" : "NO CUMPLE",
              style: "celda",
              bold: true,
              alignment: "center",
              color: r.cumple === null ? GRIS : "#FFFFFF",
              fillColor: r.cumple === null ? "#F5F5F5" : r.cumple ? VERDE : ROJO,
            },
          ],
        ],
      },
      layout: { hLineColor: () => "#7F7F7F", vLineColor: () => "#7F7F7F", hLineWidth: () => 0.5, vLineWidth: () => 0.5 },
    },
    { text: "Primera pasada contra objetivo", style: "tituloSeccion" },
    { svg: graficaPrimeraPasada(r, ANCHO) },
  ];
};

const reporteDiaProduccionPdf = ({ grupos, empresa = {}, etiquetaRango, ahora, usuarioNombre, titulo = "REPORTE DE PRODUCCIÓN", conPrimeraPasada = true }) => {
  const categorias = categoriasDelReporte(grupos);
  const filas = filasDelReporte(grupos);
  const evaluadas = filas.filter((f) => f.cumple !== null);
  const cumplen = evaluadas.filter((f) => f.cumple).length;
  const planeadas = filas.reduce((a, f) => a + f.planeadas, 0);
  const produccionReal = filas.reduce((a, f) => a + f.produccionReal, 0);
  const okPiezas = filas.reduce((a, f) => a + (f.categorias.find((c) => c.etiqueta === "OK")?.llegaron || 0), 0);
  const okGlobal = produccionReal ? (okPiezas / produccionReal) * 100 : null;
  const anchoDistribucion = 430;
  const anchoTotal = ANCHO - anchoDistribucion - 20;

  return {
    pageOrientation: "landscape",
    pageSize: "LETTER",
    pageMargins: [20, 20, 20, 28],
    footer: (actual, total) => ({
      columns: [
        { text: `Imprimió: ${usuarioNombre || "—"}`, fontSize: 7, color: GRIS, margin: [20, 6, 0, 0] },
        { text: `${actual} / ${total}`, alignment: "right", fontSize: 7, color: GRIS, margin: [0, 6, 20, 0] },
      ],
    }),
    content: [
      {
        columns: [
          empresa.logo?.base64Completa ? { image: "logo", fit: [64, 64], width: 80 } : { text: "", width: 80 },
          {
            width: "*",
            stack: [
              { text: empresa.nombre || "-", fontSize: 18, bold: true, color: TINTA, alignment: "center" },
              { text: `RFC: ${empresa.rfc || "-"}`, fontSize: 8, color: GRIS, alignment: "center" },
              { text: titulo, fontSize: 13, bold: true, color: PETROLEO, alignment: "center", margin: [0, 4, 0, 0] },
            ],
          },
          {
            width: 170,
            stack: [
              { text: "PERIODO", fontSize: 7, bold: true, color: GRIS, alignment: "right" },
              { text: etiquetaRango || "—", fontSize: 9, alignment: "right" },
              { text: "IMPRIMIÓ", fontSize: 7, bold: true, color: GRIS, alignment: "right", margin: [0, 4, 0, 0] },
              { text: usuarioNombre || "—", fontSize: 9, alignment: "right" },
              { text: moment(ahora).format("DD/MM/YYYY HH:mm"), fontSize: 8, color: GRIS, alignment: "right" },
            ],
          },
        ],
      },
      { canvas: [{ type: "line", x1: 0, y1: 4, x2: ANCHO, y2: 4, lineWidth: 2, lineColor: CIAN }], margin: [0, 4, 0, 8] },
      {
        margin: [0, 0, 0, 8],
        table: {
          widths: ["*", "*", "*", "*"],
          body: [
            [
              tarjeta("Piezas planeadas", miles(planeadas)),
              tarjeta("Producción real", miles(produccionReal)),
              tarjeta("% OK global", pct(okGlobal), CIAN),
              tarjeta("Órdenes que cumplen objetivo", `${cumplen} de ${evaluadas.length}`, evaluadas.length && cumplen === evaluadas.length ? VERDE : evaluadas.length ? ROJO : GRIS),
            ],
          ],
        },
        layout: { fillColor: () => "#F0FBFC", hLineColor: () => "#FFFFFF", vLineColor: () => "#FFFFFF", hLineWidth: () => 4, vLineWidth: () => 4 },
      },
      filas.length ? tablaReporte(filas, categorias) : { text: "No hay órdenes en este periodo.", color: GRIS },
      ...(filas.length
        ? [
            { text: "% OK real contra objetivo", style: "tituloSeccion", pageBreak: "before" },
            { svg: graficaOkVsObjetivo(filas, ANCHO) },
            {
              margin: [0, 6, 0, 0],
              columns: [
                { width: anchoDistribucion, stack: [{ text: "Distribución de resultados por orden", style: "tituloSeccion" }, { svg: graficaDistribucion(filas, categorias, anchoDistribucion) }] },
                { width: 20, text: "" },
                { width: anchoTotal, stack: [{ text: "Total de producción real", style: "tituloSeccion" }, { svg: graficaTotal(filas, categorias, anchoTotal) }] },
              ],
            },
          ]
        : []),
      ...(conPrimeraPasada ? ordenesDelReporte(grupos).flatMap(paginaPrimeraPasada) : []),
    ],
    images: empresa.logo?.base64Completa ? { logo: empresa.logo.base64Completa } : {},
    defaultStyle: { font: "Roboto", fontSize: 8 },
    styles: {
      encabezado: { fontSize: 6.5, bold: true, alignment: "center" },
      celda: { fontSize: 7.5 },
      tituloSeccion: { fontSize: 11, bold: true, color: PETROLEO, margin: [0, 8, 0, 6] },
    },
  };
};

export default reporteDiaProduccionPdf;
